<template>
  <div class="tender-potential-bidders">
    <div class="tender-potential-bidders__header">
      <div class="tender-potential-bidders__subtitle">
        {{ $t('tender-details.potential-bidders.title') }}
        <span v-if="showRatio">
          ({{
            $t('tender-details.potential-bidders.submitted-ratio', {
              submitted: submissionRatio.submitted,
              total: submissionRatio.total,
            })
          }})
        </span>
        <span v-else-if="!tenderError"> ({{ biTender ? biTender.interested_tenderers.length : 0 }}) </span>
      </div>
      <BaseTag
        v-if="showRatio"
        preset="soft"
        :tone="ratioTone"
        v-tooltip="$t('tender-details.potential-bidders.submission-rate-tooltip')"
      >
        {{ $t('tender-details.potential-bidders.submission-rate', { rate: ratioPercent }) }}
      </BaseTag>
    </div>
    <BaseCircularLoadingBar v-if="pending && !biTender" :size="14" :width="2" color="accent" />
    <div v-else-if="tenderError" class="tender-potential-bidders__unavailable">
      {{ $t('tender-details.bidders.unavailable') }}
    </div>
    <div v-else-if="biTender" class="tender-potential-bidders__content">
      <div class="tender-potential-bidders__results" v-if="biTender && biTender.interested_tenderers.length > 0">
        <template v-for="potentialBidder in sortedTenderers">
          <BasePotentialBidder
            :potentialBidder="potentialBidder"
            :key="potentialBidder.id"
            :source="biTender.source"
            :hasWinner="biTender.awarded_to && biTender.awarded_to.length > 0"
            :submitted="showRatio && isPotentialBidderSubmitted(potentialBidder, biTender.bids)"
            actionable
            @add-competitor="onAddCompetitorClicked"
            @edit-competitor="onEditCompetitorClicked"
            @add-winner="onAddWinnerClicked"
            @action-succeeded="onActionSucceeded"
          />
        </template>
      </div>
      <div v-else class="tender-potential-bidders__empty">
        {{ $t('tender-details.potential-bidders.none') }}
      </div>
    </div>
    <div v-else class="tender-potential-bidders__empty">{{ $t('tender-details.potential-bidders.none') }}</div>
    <AddCompetitorDialog ref="addCompetitorDialog" @action-succeeded="onActionSucceeded" />
    <EditCompetitorDialog ref="editCompetitorDialog" @action-succeeded="onActionSucceeded" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { isPotentialBidderSubmitted, computeSubmissionRatio, isTenderClosed } from '@/util/potential-bidders-util';
import BasePotentialBidder from '@/components/base/BasePotentialBidder.vue';
import BaseCircularLoadingBar from '@/components/base/BaseCircularLoadingBar.vue';
import BaseTag from '@/components/base/BaseTag.vue';
import AddCompetitorDialog from '@/views/business-intelligence/AddCompetitorDialog.vue';
import EditCompetitorDialog from '@/views/business-intelligence/EditCompetitorDialog.vue';

export default {
  name: 'TenderPotentialBidders',
  components: {
    BasePotentialBidder,
    BaseCircularLoadingBar,
    BaseTag,
    AddCompetitorDialog,
    EditCompetitorDialog,
  },
  computed: {
    ...mapGetters('biDashboard', { biTender: 'tender', pending: 'tenderPending', tenderError: 'tenderError' }),
    sortedTenderers() {
      if (!this.biTender) return [];
      const rank = (b) => (b.is_competitor ? 0 : b.is_tenant ? 1 : b.is_existing_competitor ? 2 : 3);
      return [...this.biTender.interested_tenderers].sort((a, b) => rank(a) - rank(b));
    },
    submissionRatio() {
      if (!this.biTender) return { submitted: 0, total: 0, ratio: 0 };
      return computeSubmissionRatio(this.biTender.interested_tenderers, this.biTender.bids);
    },
    showRatio() {
      // A failed refetch keeps the previous data in the store; do not show a ratio next to "unavailable".
      if (!this.biTender || this.tenderError) return false;
      const bids = this.biTender.bids;
      const hasBidsRevealed = Array.isArray(bids) && bids.length > 0;
      const tenderClosed = hasBidsRevealed || isTenderClosed(this.biTender.status);
      return tenderClosed && this.submissionRatio.total > 0;
    },
    ratioPercent() {
      return Math.round(this.submissionRatio.ratio * 100);
    },
    ratioTone() {
      const r = this.submissionRatio.ratio;
      if (r >= 0.66) return 'success';
      if (r >= 0.33) return 'warning';
      return 'destructive';
    },
  },
  async mounted() {
    await this.getAllCompetitors();
  },
  methods: {
    ...mapActions('biDashboard', ['getTender', 'getAllCompetitors']),
    ...mapActions('biTenant', ['createAward']),
    isPotentialBidderSubmitted,
    onAddCompetitorClicked(name) {
      this.$refs.addCompetitorDialog.open(name);
    },
    onEditCompetitorClicked(competitor, name) {
      this.$refs.editCompetitorDialog.open(competitor, name);
    },
    onAddWinnerClicked(potentialBidder) {
      this.createAward({
        tenderId: this.biTender.id,
        supplierName: potentialBidder.organization_name,
        amount: potentialBidder.amount || 0,
      })
        .then(() => {
          this.$toast.success(this.$t('page.business-intelligence.manage-dialog.awards.create-success'));
          this.$emit('action-succeeded');
        })
        .catch((error) => {
          this.$toast.error(error);
        });
    },
    onActionSucceeded() {
      this.$emit('action-succeeded');
    },
  },
};
</script>

<style lang="scss">
.tender-potential-bidders {
  width: 100%;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }

  &__subtitle {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    font-weight: 800;
    font-size: 14px;
  }

  &__content {
    margin-top: 8px;
  }

  &__empty,
  &__unavailable {
    margin-top: 8px;
    color: $color-neutral;
    font-size: $font-size-small;
  }

  &__results {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 8px;
  }
}
</style>
